import React from "react"
import { graphql } from "gatsby"
import "./highlights.module.scss"

import { ButtonBuilder } from "_types/Button"
import { SingleHighlight } from "./highlights-types"

import { ButtonParser } from "components/button/ButtonParser"
import { ContentHeader } from "components/content/ContentHeader"
import { SingleCounterHighlight } from "./SingleCounterHighlight"
import { useInView } from "react-intersection-observer"

interface HighlightsProps {
  buttons?: ButtonBuilder[]
  eyebrowText?: string
  heading?: string
  highlights: SingleHighlight[]
  subHeading?: string
}

export const Highlights = ({
  buttons,
  eyebrowText,
  heading,
  highlights,
  subHeading,
}: HighlightsProps) => {
  const isCounters = highlights.some(
    ({ elements }) => !!elements.counter_end_value?.value
  )

  const getHighlightsCountClass = () => {
    switch (highlights.length) {
      case 1:
        return `${prefix}__count--one`
      case 2:
        return `${prefix}__count--two`
      case 3:
        return `${prefix}__count--three`
      case 6:
        return `${prefix}__count--six`
      case 4:
      default:
        return `${prefix}__count--four`
    }
  }

  const prefix: string = isCounters ? "fr-counters" : "fr-highlights"
  const evenOddClass =
    highlights.length % 2
      ? `${prefix}__wrapper--odd`
      : `${prefix}__wrapper--even`
  const highlightsCountClass = getHighlightsCountClass()

  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  })
  const styleSubHeadingAsIntroParagraph =
    !!subHeading && subHeading.length > 150

  const getHeading = () => {
    const hasHeading = !!heading

    if (!hasHeading) return <></>

    if (!styleSubHeadingAsIntroParagraph) {
      return (
        <ContentHeader
          baseClassName={prefix}
          eyebrowText={eyebrowText}
          heading={heading}
          subHeading={subHeading}
        />
      )
    } else {
      return (
        <>
          <ContentHeader
            baseClassName={prefix}
            eyebrowText={eyebrowText}
            heading={heading}
          />
          {styleSubHeadingAsIntroParagraph && (
            <div className={`${prefix}__intro-paragraph`}>
              <p className="u-text--align-left">{subHeading}</p>
            </div>
          )}
        </>
      )
    }
  }

  return (
    <section className={`${prefix} u-content-block-padding`}>
      <div className="fr-container">
        {getHeading()}
        <div
          className={`${prefix}__wrapper ${evenOddClass} ${highlightsCountClass}`}
          ref={ref}
        >
          {highlights.map(({ elements, id }) => {
            return (
              <SingleCounterHighlight
                elements={elements}
                inView={inView}
                key={id}
              />
            )
          })}
        </div>
        {buttons && buttons.length > 0 && (
          <div
            className={`u-buttons-group content-block-buttons ${prefix}__buttons`}
          >
            <ButtonParser buttons={buttons} />
          </div>
        )}
      </div>
    </section>
  )
}

export const query = graphql`
  fragment HighlightGroupFragment on kontent_item_highlights_group {
    system {
      type
    }
    id
    elements {
      buttons {
        value {
          ...AnchorLinkFragment
          ...ButtonFragment
        }
      }
      eyebrow_text {
        value
      }
      heading {
        value
      }
      sub_heading {
        value
      }
      highlights {
        modular_content {
          ...HighlightFragment
        }
      }
    }
  }
`
